import { path } from 'utils'
import { z } from 'zod'

import { taskSchema } from '../pangea'
import { jsonSchema } from '../shared'
import { locationApi, metricSchema, softwareVersionSchema } from '../stitch'

export enum MessageType {
  /**
   * Handshake message, just to confirm the connection
   */
  Handshake = 'handshake',
  /**
   * Indicates the current state of the connection
   */
  Status = 'status',
  /**
   * Indicates the devices that are available and what streams they have active
   */
  Account = 'account',
  /**
   * Data from a device, including the last record id, location, and serial number
   */
  Data = 'data',
  /**
   * Identifies the software version of the device
   */
  About = 'about',
  /**
   * Heartbeat message, sent every 5 seconds
   */
  Heartbeat = 'heartbeat',
  /**
   * Unknown message type, used for messages that failed to parse
   */
  Unknown = 'unknown'
}

export enum ConnectionStatus {
  Disconnected = 'disconnected',
  Connected = 'connected',
  Authenticated = 'authenticated',
  Following = 'following'
}

const dataSchema = z.object({
  recordId: z.number(),
  location: locationApi.responseSchema.pick({ heading: true, hitch: true }).optional(),
  speed: metricSchema.omit({ label: true }).optional(),
  serialNumber: z.number(),
  type: z.literal(MessageType.Data)
})

const aboutSchema = z.object({
  type: z.literal(MessageType.About),
  version: softwareVersionSchema,
  serialNumber: z.number()
})

const accountSchema = z.object({
  type: z.literal(MessageType.Account),
  devices: z.array(
    z.object({
      client: z.string(),
      farm: z.string(),
      field: z.string(),
      serialNumber: z.number(),
      streamId: z.string(),
      crop: z.string(),
      season: z.number(),
      taskIndex: z.number(),
      task: z.object({
        key: taskSchema,
        name: z.string()
      })
    })
  )
})

const statusSchema = z.object({
  status: z.enum(ConnectionStatus),
  type: z.literal(MessageType.Status)
})

const heartbeatSchema = z.object({
  type: z.literal(MessageType.Heartbeat)
})

const handshakeSchema = z.object({
  gitCommitHash: z.string(),
  orgCode: z.string(),
  type: z.literal(MessageType.Handshake)
})

const unknownSchema = z.object({
  type: z.literal(MessageType.Unknown)
})

/**
 * The websocket api for getting live field data from the Emissary service
 */
export const emissaryWebsocketApi = {
  pathFor: (p: { orgCode: string; domain: string; access_token: string }) =>
    path(`wss://live.${p.domain}`, {
      orgCode: p.orgCode,
      access_token: p.access_token
    }),
  responseSchema: jsonSchema.pipe(
    z.object({
      message: z.array(
        z
          .discriminatedUnion('type', [
            handshakeSchema,
            statusSchema,
            accountSchema,
            dataSchema,
            heartbeatSchema,
            aboutSchema,
            unknownSchema
          ])
          .catch((e) => ({
            type: MessageType.Unknown,
            value: e.input as unknown,
            error: e.error
          }))
      )
    })
  )
}

export namespace Emissary {
  export type Data = z.infer<typeof dataSchema>
  export type Account = z.infer<typeof accountSchema>
  export type Status = z.infer<typeof statusSchema>
  export type Heartbeat = z.infer<typeof heartbeatSchema>
  export type Handshake = z.infer<typeof handshakeSchema>
  export type Unknown = z.infer<typeof unknownSchema>
  export type About = z.infer<typeof aboutSchema>
  export type Message = z.infer<typeof emissaryWebsocketApi.responseSchema>
}
